import { useState, useEffect } from "react";
import { useProfileFetch } from "./useProfileFetch";
import { useProfileUpdates } from "./useProfileUpdates";
import { useTelegramConnection } from "./useTelegramConnection";
import { useSearchParams } from "react-router-dom";
import { calculateProfileCompleteness, ProfileCompletenessResult } from "@/utils/profileUtils";
import { Service } from "@/types/dashboard";

export interface ProfileData {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  country?: string | null;
  whatsapp_number?: string | null;
  company_name?: string | null;
  company_description?: string | null;
  address?: string | null;
  phone?: string | null;
  preferred_messaging?: string | null;
  telegram_chat_id?: number | null;
  profile_image_url?: string | null;
  banner_image_url?: string | null;
  profession?: string | null;
  profile_bio?: string | null;
  currency?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  last_update?: string | null;
  preferred_language?: string | null;
  services?: Service[] | null;
}

export const useProfileSettings = () => {
  const { profile, loading, fetchProfile, setProfile } = useProfileFetch();
  const { updateProfile } = useProfileUpdates(profile);
  const { 
    connectingTelegram,
    connectionAttempted,
    telegramDeeplink,
    connectTelegram
  } = useTelegramConnection();

  const handleUpdateProfile = async (updates: Partial<ProfileData>) => {
    const updatedProfile = await updateProfile(updates);
    
    if (updatedProfile) {
      setProfile({
        ...profile,
        ...updatedProfile,
        email: profile?.email
      });
      return true;
    }
    
    return false;
  };

  const pollForTelegramConnection = async () => {
    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 3000;
    
    const checkConnection = async () => {
      attempts++;
      console.log(`Checking Telegram connection (attempt ${attempts}/${maxAttempts})...`);
      
      const profileData = await fetchProfile();
      
      if (profileData?.telegram_chat_id) {
        console.log("Telegram connection successful!");
        return true;
      }
      
      if (attempts >= maxAttempts) {
        console.log("Max polling attempts reached");
        return false;
      }
      
      setTimeout(checkConnection, pollInterval);
    };
    
    setTimeout(checkConnection, pollInterval);
  };

  const handleConnectTelegram = async () => {
    const success = await connectTelegram();
    if (success) {
      pollForTelegramConnection();
    }
  };

  return {
    profile,
    loading,
    connectingTelegram,
    telegramDeeplink,
    connectionAttempted,
    fetchProfile,
    updateProfile: handleUpdateProfile,
    connectTelegram: handleConnectTelegram
  };
};

export function useProfileCompleteness(services: Service[]): ProfileCompletenessResult | null {
  const { profile } = useProfileSettings();
  if (!profile) return null;
  return calculateProfileCompleteness(profile, services);
}
